import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usersAPI } from '../../services/api';
import { FiUser, FiMail, FiPhone, FiMapPin, FiPlus, FiTrash2, FiEdit, FiUpload, FiEyeOff } from 'react-icons/fi';
import toast from 'react-hot-toast';
import './Profile.css';

const Profile = () => {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('personal');
  const [profile, setProfile] = useState({});
  const [skills, setSkills] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [education, setEducation] = useState([]);
  const [newSkill, setNewSkill] = useState('');
  const [hideFromEmployer, setHideFromEmployer] = useState(false);

  useEffect(() => {
    if (user?.id) loadProfile();
  }, [user]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const { data } = await usersAPI.get(user.id);
      const p = data.data || data.user || data;
      setProfile(p);
      setHideFromEmployer(!p.show_me_to_previous_employer);
    } catch (e) {
      setProfile(user || {});
    }
    try {
      const { data } = await usersAPI.skills(user.id);
      setSkills(data.data || data.skills || (Array.isArray(data) ? data : []));
    } catch (e) {}
    try {
      const { data } = await usersAPI.experiences(user.id);
      setExperiences(data.data || data.experiences || (Array.isArray(data) ? data : []));
    } catch (e) {}
    try {
      const { data } = await usersAPI.education(user.id);
      setEducation(data.data || data.education || (Array.isArray(data) ? data : []));
    } catch (e) {}
    setLoading(false);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await usersAPI.update(user.id, {
        first_name: profile.first_name, last_name: profile.last_name,
        phone: profile.phone, bio: profile.bio, location: profile.location,
        title: profile.title, show_me_to_previous_employer: !hideFromEmployer,
      });
      await refreshUser();
      toast.success('Profile updated');
    } catch (err) {
      toast.error('Failed to update');
    }
    setSaving(false);
  };

  const addSkill = async () => {
    if (!newSkill.trim()) return;
    try {
      await usersAPI.addSkill(user.id, { name: newSkill.trim() });
      setNewSkill('');
      loadProfile();
      toast.success('Skill added');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to add skill');
    }
  };

  const removeSkill = async (skillId) => {
    try {
      await usersAPI.removeSkill(user.id, skillId);
      setSkills(skills.filter(s => s.id !== skillId));
      toast.success('Skill removed');
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('resume', file);
    try {
      await usersAPI.uploadResume(user.id, formData);
      toast.success('Resume uploaded');
      loadProfile();
    } catch (err) {
      toast.error('Upload failed');
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div className="profile-page">
      <div className="container" style={{ maxWidth: '900px' }}>
        <div className="page-header">
          <h1>My Profile</h1>
          <p>Manage your professional profile and preferences</p>
        </div>

        <div className="tabs">
          <button className={`tab ${tab === 'personal' ? 'active' : ''}`} onClick={() => setTab('personal')}>Personal Info</button>
          <button className={`tab ${tab === 'skills' ? 'active' : ''}`} onClick={() => setTab('skills')}>Skills</button>
          <button className={`tab ${tab === 'experience' ? 'active' : ''}`} onClick={() => setTab('experience')}>Experience</button>
          <button className={`tab ${tab === 'education' ? 'active' : ''}`} onClick={() => setTab('education')}>Education</button>
          <button className={`tab ${tab === 'resume' ? 'active' : ''}`} onClick={() => setTab('resume')}>Resume</button>
        </div>

        {tab === 'personal' && (
          <form onSubmit={handleUpdateProfile} className="card" style={{ padding: '2rem' }}>
            <div className="profile-avatar-section">
              <div className="profile-avatar">{(profile.first_name || 'U')[0]}</div>
              <div>
                <h3>{profile.first_name} {profile.last_name}</h3>
                <p style={{ color: 'var(--text-muted)' }}>{profile.title || profile.email}</p>
              </div>
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label>First Name</label>
                <input type="text" value={profile.first_name || ''} onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input type="text" value={profile.last_name || ''} onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} />
              </div>
            </div>

            <div className="form-group">
              <label>Professional Title</label>
              <input type="text" placeholder="e.g. Senior Software Developer" value={profile.title || ''} onChange={(e) => setProfile({ ...profile, title: e.target.value })} />
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={profile.email || ''} disabled style={{ opacity: 0.6 }} />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input type="tel" value={profile.phone || ''} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
              </div>
            </div>

            <div className="form-group">
              <label>Location</label>
              <input type="text" placeholder="e.g. Johannesburg, Gauteng" value={profile.location || ''} onChange={(e) => setProfile({ ...profile, location: e.target.value })} />
            </div>

            <div className="form-group">
              <label>Bio</label>
              <textarea rows="4" placeholder="Tell recruiters about yourself..." value={profile.bio || ''} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} />
            </div>

            <div className="form-group">
              <div className="toggle-wrapper" onClick={() => setHideFromEmployer(!hideFromEmployer)} style={{ cursor: 'pointer' }}>
                <div className={`toggle ${hideFromEmployer ? 'active' : ''}`} />
                <div>
                  <div style={{ fontWeight: 500 }}><FiEyeOff style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />Hide from current employer</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Your profile won't be visible to your current employer's recruiters</div>
                </div>
              </div>
            </div>

            <button type="submit" className="btn btn-primary btn-lg" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
          </form>
        )}

        {tab === 'skills' && (
          <div className="card" style={{ padding: '2rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Skills</h3>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
              <input type="text" placeholder="Add a skill (e.g. Python, React, SQL)" value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())} />
              <button className="btn btn-primary" onClick={addSkill}><FiPlus /> Add</button>
            </div>
            <div className="skills-list">
              {skills.map((s) => (
                <span key={s.id} className="skill-tag" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                  {typeof s === 'string' ? s : s.name}
                  <button onClick={() => removeSkill(s.id)} style={{ background: 'none', color: 'inherit', opacity: 0.6, fontSize: '0.9rem' }}><FiTrash2 /></button>
                </span>
              ))}
              {skills.length === 0 && <p style={{ color: 'var(--text-muted)' }}>No skills added yet. Add skills to improve your job matching.</p>}
            </div>
          </div>
        )}

        {tab === 'experience' && (
          <div className="card" style={{ padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h3>Work Experience</h3>
            </div>
            {experiences.length > 0 ? experiences.map((exp) => (
              <div key={exp.id} className="exp-item">
                <div className="exp-dot" />
                <div>
                  <h4>{exp.title || exp.position}</h4>
                  <div className="exp-company">{exp.company_name || exp.company}</div>
                  <div className="exp-date">{exp.start_date} - {exp.end_date || 'Present'}</div>
                  {exp.description && <p>{exp.description}</p>}
                </div>
              </div>
            )) : <p style={{ color: 'var(--text-muted)' }}>No experience added. Your experience helps recruiters match you to the right roles.</p>}
          </div>
        )}

        {tab === 'education' && (
          <div className="card" style={{ padding: '2rem' }}>
            <h3 style={{ marginBottom: '1.25rem' }}>Education</h3>
            {education.length > 0 ? education.map((edu) => (
              <div key={edu.id} className="exp-item">
                <div className="exp-dot" style={{ background: 'var(--accent-purple)' }} />
                <div>
                  <h4>{edu.degree || edu.qualification}</h4>
                  <div className="exp-company">{edu.institution || edu.school}</div>
                  <div className="exp-date">{edu.start_date} - {edu.end_date || 'Present'}</div>
                </div>
              </div>
            )) : <p style={{ color: 'var(--text-muted)' }}>No education added yet.</p>}
          </div>
        )}

        {tab === 'resume' && (
          <div className="card" style={{ padding: '2rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Resume / CV</h3>
            {profile.resume_path && (
              <div className="resume-current">
                <span>Current resume: {profile.resume_path.split('/').pop()}</span>
              </div>
            )}
            <label className="upload-area">
              <FiUpload style={{ fontSize: '2rem', color: 'var(--text-muted)' }} />
              <p>Click to upload your resume (PDF, DOC, DOCX)</p>
              <input type="file" accept=".pdf,.doc,.docx" hidden onChange={handleResumeUpload} />
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
